import React, { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save } from "lucide-react";
import { ClientApi } from "@/api/client.api";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import countries from "@/constants/array/country_city.json";
import { LoaderButton } from "@/components/ui/loader-button";
import {
  clientContactNumberSchema,
  sanitizeClientContactNumber,
} from "@/utils/client-contact.utils";

const CURRENCY_OPTIONS = [
  { id: 1, label: "United State Dollar", value: "USD" },
  { id: 2, label: "EURO", value: "EUR" },
  { id: 3, label: "British Pound", value: "GBP" },
  { id: 4, label: "Indian Rupee", value: "INR" },
  { id: 5, label: "United Arab Emirates Dirham", value: "AED" },
  { id: 6, label: "Ethiopian Birr", value: "ETB" },
];

const ClientUpdateSchema = Yup.object().shape({
  clientName: Yup.string().required("Client Name is required"),
  clientContactNumber: clientContactNumberSchema,
  clientEmail: Yup.string()
    .email("Invalid email address")
    .required("Client Email is required"),
  clientCountry: Yup.string()
    .trim()
    .required("Country is required"),
  currency: Yup.string().required("Currency is required"),
  clientAddress: Yup.string().required("Address is required"),
});

const resolveCountryName = (country?: string | null) => {
  if (!country?.trim()) return "";
  return (
    (countries as any[])?.find(
      (c) => c.name.toLowerCase() === country.trim().toLowerCase()
    )?.name || ""
  );
};

export const EditClientSheet = ({ open, onOpenChange, client, onUpdate }) => {
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm({
    resolver: yupResolver(ClientUpdateSchema),
    defaultValues: {
      clientName: "",
      clientContactNumber: "",
      clientEmail: "",
      clientCountry: "",
      currency: "USD",
      clientAddress: "",
    },
  });

  useEffect(() => {
    if (client) {
      form.reset({
        clientName: client.name || "",
        clientContactNumber: sanitizeClientContactNumber(
          client.contact || ""
        ),
        clientEmail: client.email || "",
        // Only keep country if it matches a dropdown option; otherwise force empty so required validation runs
        clientCountry: resolveCountryName(client.country),
        currency: client.currency || "USD",
        clientAddress: client.address || "",
      });
    }
  }, [client, form]);

  const handleSave = async (formData) => {
    if (!client) return;
    try {
      setIsSaving(true);
      const currencyId =
        CURRENCY_OPTIONS.find((c) => c.value === formData.currency)?.id || 1;
      const payload = {
        clientName: formData.clientName,
        clientAddress: formData.clientAddress,
        clientContactNumber: formData.clientContactNumber,
        clientEmail: formData.clientEmail,
        clientCountry: formData.clientCountry,
        currencyId,
      };
      await ClientApi.updateClient(client.id, payload);
      toast.success("Client updated successfully");
      onUpdate();
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to update client");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Client</SheetTitle>
          <SheetDescription>
            Make changes to your client here. Click save when you're done.
          </SheetDescription>
        </SheetHeader>
        <div className="py-4">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSave)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="clientName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Name*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="clientContactNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Number*</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. 501234567"
                        inputMode="numeric"
                        maxLength={15}
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            sanitizeClientContactNumber(e.target.value)
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="clientEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Email*</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. client@email.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="clientCountry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country*</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent onWheel={(e) => e.stopPropagation()}>
                        <div className="max-h-[200px] overflow-y-auto">
                          {(countries as any)?.map((country) => (
                            <SelectItem
                              key={country?.name}
                              value={country?.name}
                            >
                              {country?.name}
                            </SelectItem>
                          ))}
                        </div>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency*</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CURRENCY_OPTIONS.map((currency) => (
                          <SelectItem
                            key={currency.value}
                            value={currency.value}
                          >
                            {currency.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="clientAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end">
                <LoaderButton
                  type="submit"
                  className="flex items-center gap-2"
                  loading={isSaving}
                >
                  <Save className="h-4 w-4" /> Save Changes
                </LoaderButton>
              </div>
            </form>
          </Form>
        </div>
      </SheetContent>
    </Sheet>
  );
};
